import React, { useEffect, useState } from "react";
import { Container, Stack, Box, Button, Rating } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Dispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { setChosenProduct, setRestaurant } from "./slice";
import { Product } from "../../../lib/types/product";
import { createSelector } from "reselect";
import { retrieveChosenProduct, retrieveRestaurant } from "./selector";
import ProductService from "../../services/ProductService";
import { useHistory, useParams } from "react-router-dom";
import MemberService from "../../services/MemberService";
import { Member } from "../../../lib/types/member";
import { serverApi } from "../../../lib/config";
import { CartItem } from "../../../lib/types/search";
import { useGlobals } from "../../hooks/useGlobal";
import ReviewModal from "../../components/review/ReviewModal";
import { Messages } from "../../../lib/config";
import { sweetErrorHandling, sweetTopSuccessAlert } from "../../../lib/sweetAlert";

const actionDispatch = (dispatch: Dispatch) => ({
  setRestaurant: (data: Member) => dispatch(setRestaurant(data)),
  setChosenProduct: (data: Product | null) => dispatch(setChosenProduct(data)),
});

const chosenProductRetriever = createSelector(
  retrieveChosenProduct,
  (chosenProduct) => ({ chosenProduct })
);
const restaurantRetriever = createSelector(retrieveRestaurant, (restaurant) => ({
  restaurant,
}));

interface ChosenProductProps {
  onAdd: (item: CartItem) => void;
}

export default function ChosenProduct(props: ChosenProductProps) {
  const { onAdd } = props;
  const { productId } = useParams<{ productId: string }>();
  const history = useHistory();
  const { setRestaurant, setChosenProduct } = actionDispatch(useDispatch());
  const { chosenProduct } = useSelector(chosenProductRetriever);
  const { restaurant } = useSelector(restaurantRetriever);
  const { authMember } = useGlobals();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    setChosenProduct(null);

    const product = new ProductService();
    product
      .getProduct(productId)
      .then((data) => {
        if (!alive) return;
        setChosenProduct(data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        if (!alive) return;
        setError("Book details could not be loaded.");
        setLoading(false);
      });

    const member = new MemberService();
    member
      .getRestaurant()
      .then((data) => {
        if (alive) setRestaurant(data);
      })
      .catch((err) => console.log(err));

    return () => {
      alive = false;
    };
  }, [productId]);

  if (loading) {
    return (
      <div className="chosen-product booclub-detail">
        <Container>
          <Box className="detail-status">Loading book details...</Box>
        </Container>
      </div>
    );
  }

  if (error || !chosenProduct) {
    return (
      <div className="chosen-product booclub-detail">
        <Container>
          <Box className="detail-status">
            {error || "Book not found."}
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/products")}
              sx={{ ml: 2 }}
            >
              Back to Products
            </Button>
          </Box>
        </Container>
      </div>
    );
  }

  const imagePath = chosenProduct.productImages?.[0]
    ? `${serverApi}/${chosenProduct.productImages[0]}`
    : "/img/banner.webp";

  return (
    <div className={"chosen-product booclub-detail"}>
      <Container className={"product-container"}>
        <Button
          className="detail-back-btn"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push("/products")}
        >
          Back
        </Button>

        <Stack className="booclub-detail-card" direction={{ xs: "column", md: "row" }} spacing={4}>
          <Box className="detail-cover-wrap">
            <img className="detail-cover" src={imagePath} alt={chosenProduct.productName} />
          </Box>

          <Stack className={"chosen-product-info"}>
            <Box className={"info-box"}>
              <span className="genre-chip">{chosenProduct.productGenre || "BOOK"}</span>
              <strong className={"product-name"}>{chosenProduct.productName}</strong>
              <span className={"resto-name"}>{restaurant?.memberNick || "BooClub"}</span>
              <Box className={"rating-box"}>
                <Rating
                  name="book-rating"
                  value={chosenProduct?.productRating || 0}
                  precision={0.5}
                  readOnly
                />
                <div className={"evaluation-box"}>
                  <div className={"product-view"}>
                    <RemoveRedEyeIcon sx={{ mr: "10px" }} />
                    <span>{chosenProduct?.productViews} views</span>
                  </div>
                </div>
              </Box>

              <Stack direction="row" className="meta-grid" spacing={3}>
                <Box>
                  <div className="meta-label">FORMAT</div>
                  <strong>{chosenProduct.productFormat || "STANDARD"}</strong>
                </Box>
                <Box>
                  <div className="meta-label">STATUS</div>
                  <strong className="in-stock">
                    {(chosenProduct.productLeftCount || 0) > 0 ? "In Stock" : "Out of Stock"}
                  </strong>
                </Box>
                <Box>
                  <div className="meta-label">GENRE</div>
                  <strong>{chosenProduct.productGenre}</strong>
                </Box>
              </Stack>

              <p className="by-author">By {chosenProduct.productAuthor || "Unknown"}</p>
              <p className={"product-desc"}>
                {chosenProduct?.productDesc || "No description"}
              </p>

              <div className={"product-price"}>
                <span>${chosenProduct?.productPrice}</span>
              </div>

              <div className={"button-box"}>
                <Button
                  className="add-basket-btn"
                  variant="contained"
                  onClick={(e) => {
                    onAdd({
                      _id: chosenProduct._id,
                      quantity: 1,
                      name: chosenProduct.productName,
                      price: chosenProduct.productPrice,
                      image: chosenProduct.productImages[0],
                    });
                    e.stopPropagation();
                    sweetTopSuccessAlert("Added to basket", 700);
                  }}
                >
                  ADD TO BASKET
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    if (!authMember) {
                      sweetErrorHandling(Messages.error2);
                      return;
                    }
                    setReviewOpen(true);
                  }}
                >
                  Write Review
                </Button>
              </div>
            </Box>
          </Stack>
        </Stack>
      </Container>
      <ReviewModal
        open={reviewOpen}
        product={chosenProduct}
        onClose={() => setReviewOpen(false)}
      />
    </div>
  );
}
